<template>
  <HLMenuLayout
    :collapsed="collapsed"
    :width="260"
    :collapsed-width="84"
    bordered
    show-trigger
    @update:collapsed="setCollapsed($event)"
  >
    <!-- Sidebar -->
    <template #sider>
      <div
        id="sidebar-inner"
        class="sidebar-inner"
      >
        <!-- Header: Brand logo + collapse toggle -->
        <div class="sidebar-header">
          <router-link
            to="/dashboard"
            class="sidebar-brand"
          >
            <img
              src="/favicon.png"
              alt="Kaizen"
              class="sidebar-logo"
            >
            <template v-if="!collapsed">
              <div class="sidebar-brand-text">
                <HLText
                  id="sidebar-title"
                  size="sm"
                  weight="semibold"
                  html-tag="span"
                >
                  {{ header.type === 'global' ? header.title : 'Kaizen' }}
                </HLText>
                <HLText
                  id="sidebar-subtitle"
                  size="3xs"
                  weight="medium"
                  html-tag="span"
                  class="sidebar-subtitle"
                >
                  {{ header.type === 'global' ? header.subtitle : 'Test Manager' }}
                </HLText>
              </div>
            </template>
          </router-link>
        </div>

        <!-- Project Switcher: always available on global pages too, so users
             can jump into a project from the dashboard. Shows "Select Project"
             when no current project is in context. -->
        <ProjectSwitcher :collapsed="collapsed" />

        <!-- Main Navigation -->
        <div class="sidebar-nav">
          <HLMenu
            id="sidebar-main-menu"
            :options="visibleMenuOptions"
            :value="activeKey"
            :collapsed="collapsed"
            :root-indent="24"
            :tooltip-variant="TOOLTIP_VARIANT"
            aria-label="Main navigation"
            @update:value="handleNavSelect"
          />
        </div>

        <HLDivider
          id="sidebar-divider"
          class="!my-0"
        />

        <!-- Footer -->
        <div class="sidebar-footer">
          <!-- Report a Project Bug -->
          <div
            class="sidebar-action"
            :class="{ 'sidebar-action--collapsed': collapsed }"
          >
            <HLTooltip
              trigger="hover"
              placement="right"
              :variant="TOOLTIP_VARIANT"
              :disabled="!collapsed"
            >
              <template #trigger>
                <HLButton
                  id="sidebar-raise-bug"
                  variant="secondary"
                  color="primary"
                  size="xs"
                  :full-width="!collapsed"
                  :aria-label="collapsed ? raiseBugTooltip : undefined"
                  @click="openReportBug"
                >
                  <template #iconLeft>
                    <MaterialBugReportIcon />
                  </template>
                  <template
                    v-if="!collapsed"
                    #default
                  >
                    Report a Project Bug
                  </template>
                </HLButton>
              </template>
              <template #default>
                <span class="sidebar-tooltip-label">{{ raiseBugTooltip }}</span>
              </template>
            </HLTooltip>
          </div>

          <!-- Improve Kaizen -->
          <div
            class="sidebar-action"
            :class="{ 'sidebar-action--collapsed': collapsed }"
          >
            <HLTooltip
              trigger="hover"
              placement="right"
              :variant="TOOLTIP_VARIANT"
              :disabled="!collapsed"
            >
              <template #trigger>
                <HLButton
                  id="sidebar-send-feedback"
                  variant="secondary"
                  color="gray"
                  size="xs"
                  :full-width="!collapsed"
                  :aria-label="collapsed ? 'Improve Kaizen' : undefined"
                  @click="showFeedbackModal = true"
                >
                  <template #iconLeft>
                    <MaterialFeedbackIcon />
                  </template>
                  <template
                    v-if="!collapsed"
                    #default
                  >
                    Improve Kaizen
                  </template>
                </HLButton>
              </template>
              <template #default>
                <span class="sidebar-tooltip-label">Share feedback to improve Kaizen</span>
              </template>
            </HLTooltip>
          </div>

          <HLMenu
            id="sidebar-footer-menu"
            :options="visibleFooterMenuOptions"
            :value="activeKey"
            :collapsed="collapsed"
            :root-indent="24"
            :tooltip-variant="TOOLTIP_VARIANT"
            aria-label="Footer navigation"
            @update:value="handleFooterSelect"
          />
        </div>
      </div>
    </template>

    <!-- Main Content: min-w-0 so nested tables / flex children can use full width beside sider -->
    <template #content>
      <div class="sidebar-layout-content">
        <slot />
      </div>
    </template>
  </HLMenuLayout>

  <ReportBugModal
    :is-open="showProjectSelector"
    title="Report a Bug"
    description="Choose a project to report a bug"
    :projects="modalProjects"
    @confirm="onProjectSelectedForBug"
    @cancel="cancelProjectSelector"
  />

  <CreateBugModal
    :show="showCreateBugModal"
    :project-id="selectedProjectId"
    @update:show="showCreateBugModal = $event"
  />

  <FeedbackModal
    :show="showFeedbackModal"
    @update:show="showFeedbackModal = $event"
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { HLMenuLayout, HLMenu, HLText, HLDivider, HLButton, HLTooltip, useHLNotification } from '@platform-ui/highrise'
import { MaterialBugReportIcon, MaterialFeedbackIcon } from '@gohighlevel/ghl-icons/24/material/rounded'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'
import { useSidebarNav } from '@/composables/useSidebarNav'
import { useReportBugFlow } from '@/composables/useReportBugFlow'
import ReportBugModal from '@/components/ui/ReportBugModal.vue'
import CreateBugModal from '@/components/bugs/CreateBugModal.vue'
import ProjectSwitcher from '@/components/layout/ProjectSwitcher.vue'
import FeedbackModal from '@/components/ui/FeedbackModal.vue'
import { initNotification } from '@/composables/useToast'
import { TOOLTIP_VARIANT } from '@/constants/ui'

const router = useRouter()
const authStore = useAuthStore()
const { can } = usePermissions()

const notificationApi = useHLNotification()
initNotification(notificationApi)

const {
  showProjectSelector,
  modalProjects,
  showCreateBugModal,
  selectedProjectId,
  openReportBug,
  onProjectSelectedForBug,
  cancelProjectSelector,
} = useReportBugFlow()

const {
  header,
  navItems,
  footerItems,
  menuOptions,
  footerMenuOptions,
  routeMap,
  activeKey,
  isProjectContext,
} = useSidebarNav()

const COLLAPSE_BREAKPOINT = 1024
const isSmallScreen = () => typeof window !== 'undefined' && window.innerWidth < COLLAPSE_BREAKPOINT
const userPreferredCollapsed = ref(false)
const collapsed = ref(isSmallScreen())
const showFeedbackModal = ref(false)

// Manual toggles on larger screens become the remembered preference
const setCollapsed = (value: boolean) => {
  collapsed.value = value
  if (!isSmallScreen()) userPreferredCollapsed.value = value
}

// Keyboard shortcut: [ to toggle sidebar
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === '[' && !e.ctrlKey && !e.metaKey && !e.altKey) {
    const tag = (e.target as HTMLElement)?.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return
    setCollapsed(!collapsed.value)
  }
}

// Auto-collapse below breakpoint, restore user's preference above it
const handleResize = () => {
  collapsed.value = isSmallScreen() ? true : userPreferredCollapsed.value
}
onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', handleResize)
})
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', handleResize)
})

const raiseBugTooltip = computed(() =>
  isProjectContext.value
    ? 'Report a bug in this project'
    : 'Report a project bug (choose project if needed)'
)

function filterByPermission<T extends { key: string }>(
  options: T[],
  items: { key: string; permission?: string }[]
): T[] {
  return options.filter((opt) => {
    const item = items.find((i) => i.key === opt.key)
    if (!item?.permission) return true
    const check = can[item.permission as keyof typeof can]
    return typeof check === 'function' ? check() : true
  })
}

const visibleMenuOptions = computed(() =>
  filterByPermission(menuOptions.value, navItems.value)
)

const visibleFooterMenuOptions = computed(() =>
  filterByPermission(footerMenuOptions.value, footerItems.value)
)

function handleNavSelect(key: string) {
  const to = routeMap.value[key]
  if (to) router.push(to)
}

function handleFooterSelect(key: string) {
  if (key === 'logout') {
    authStore.logout()
    return
  }
  const to = routeMap.value[key]
  if (to) router.push(to)
}
</script>

<style scoped>
:deep(.hr-layout-sider-scroll-container) {
  overflow: hidden !important;
}

.sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--neutral-200);

  /* Menu wrapper: tight horizontal padding */
  :deep(.hr-menu-wrapper) {
    padding-left: 4px !important;
    padding-right: 4px !important;
    padding-top: 4px !important;
  }

  /* Menu items: layout and spacing */
  :deep(.hr-menu) {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  :deep(.hr-menu .hr-menu-item) {
    height: 40px;
    align-items: center;
    margin-top: 0;
  }

  /* Remove focus outline on menu items */
  :deep(.hr-menu-wrapper .hr-menu-item:focus-visible .hr-menu-item-content::before) {
    outline: none !important;
  }

  /* Icon spacing: expanded vs collapsed */
  :deep(.hr-menu:not(.hr-menu--collapsed) .hr-menu-item-content .hr-menu-item-content__icon) {
    margin-inline-end: 16px !important;
  }

  :deep(.hr-menu.hr-menu--collapsed .hr-menu-item-content .hr-menu-item-content__icon) {
    margin-inline-end: 0 !important;
  }

  /* Selected pill: expanded — white bg with primary accent on trailing edge */
  :deep(.hr-menu:not(.hr-menu--collapsed) .hr-menu-item-content--selected::before) {
    background-color: #fff;
    background-image: linear-gradient(
      to left,
      var(--primary-600) 0,
      var(--primary-600) 5px,
      #fff 5px
    );
    border-start-end-radius: 8px;
    border-end-end-radius: 8px;
  }

  /* Selected pill: collapsed — accent on inline-start */
  :deep(.hr-menu.hr-menu--collapsed .hr-menu-item-content--selected::before) {
    background-color: #fff;
    background-image: linear-gradient(
      to right,
      var(--primary-600) 0,
      var(--primary-600) 5px,
      #fff 5px
    );
    border-start-start-radius: 8px;
    border-end-start-radius: 8px;
  }

  /* Selected item: icon + text color */
  :deep(.hr-menu-item-content--selected .hr-menu-item-content__icon),
  :deep(.hr-menu-item-content--selected .hr-menu-item-content-header) {
    color: var(--primary-600);
  }

  :deep(.hr-menu-item-content--selected:hover .hr-menu-item-content__icon),
  :deep(.hr-menu-item-content--selected:hover .hr-menu-item-content-header) {
    color: var(--primary-700) !important;
  }

  :deep(.hr-menu-item-content--selected:hover::before) {
    background-color: var(--neutral-100);
    background-image: linear-gradient(
      to left,
      var(--primary-700) 0,
      var(--primary-700) 5px,
      var(--neutral-100) 5px
    );
  }

  :deep(.hr-menu.hr-menu--collapsed .hr-menu-item-content--selected:hover::before) {
    background-image: linear-gradient(
      to right,
      var(--primary-700) 0,
      var(--primary-700) 5px,
      var(--neutral-100) 5px
    );
  }
}

/* Header */
.sidebar-layout-content {
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

.sidebar-header {
  padding: 16px 12px 8px;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: inherit;
  padding: 4px;
  border-radius: 8px;
  transition: opacity 0.15s;
  margin-bottom: 4px;
}

.sidebar-brand:hover {
  opacity: 0.8;
}

.sidebar-logo {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}

.sidebar-brand-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sidebar-subtitle {
  color: var(--neutral-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Nav */
.sidebar-nav {
  flex: 1;
  padding: 0 4px;
}

/* Footer */
.sidebar-footer {
  padding: 4px 4px 8px;
}

.sidebar-footer :deep(.hr-menu-item-content-header) {
  font-size: 14px !important;
}

.sidebar-footer :deep(.hr-menu-item-content-header .hr-text) {
  --n-font-weight: var(--hr-font-weight-medium) !important;
}

.sidebar-action {
  display: flex;
  padding: 4px 6px;
}

.sidebar-action :deep(.hl-button) {
  justify-content: flex-start;
}

.sidebar-action--collapsed {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.sidebar-tooltip-label {
  font-size: 13px;
  line-height: 1.45;
}

/* Collapse toggle: bigger, visible, with hover highlight */
:deep(.hr-layout-toggle-button) {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid var(--neutral-300);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

@media screen and (max-width: 1024px) {
  :deep(.hr-layout-toggle-button){
    display: none;
  }
}  


:deep(.hr-layout-toggle-button:hover) {
  border-color: var(--primary-400);
  background: var(--primary-50);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

#sidebar-main-menu :deep(.hr-text) {
  font-weight: var(--hr-font-weight-medium) !important;
}
</style>
